import React from 'react';

import clsx from 'clsx';
import IconArrowDown from '@/assets/icons/icon-arrow-down-01.svg?react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import IconMoreHorizontalSquare from '@/assets/icons/icon-more-horizontal-square.svg?react';
import { InfoTooltip } from '@/components/tooltips/InfoTooltip/InfoTooltip';
import { StylesListItemButton } from '@/components/panels/styles/StylesListItemButton/StylesListItemButton';
import type { Button, StyleButtons } from '@builder4/shared/types/styles';
import type { ButtonSize } from '@builder4/shared/styles/constants';

interface StylesButtonStyleProps {
  className: string;
  onClick?: () => void;
  isOpenCollapse: boolean;
  onDataChange: (data: StyleButtons) => void;
  value: StyleButtons;
  onButtonSizeChange?: (size: ButtonSize) => void;
}

export const StylesButtonStyle: React.FC<StylesButtonStyleProps> = ({
  className = '',
  onClick,
  isOpenCollapse,
  onDataChange,
  value: buttonStyleData,
  onButtonSizeChange,
}) => {
  // Helper to safely get Button from StyleButtons (button1/button2/button3 are always Button, not 'px')
  const getButton = (key: 'button1' | 'button2' | 'button3'): Button => {
    return buttonStyleData[key] as Button;
  };

  const handleClick = () => {
    if (onClick) onClick();
  };

  const onChangeButton = (buttonKey: 'button1' | 'button2' | 'button3', data: Button) => {
    const next: StyleButtons = { ...buttonStyleData } as StyleButtons;
    next[buttonKey] = data;
    onDataChange(next);
  };

  return (
    <Collapsible defaultOpen={isOpenCollapse} className={clsx(className)}>
      <CollapsibleTrigger
        onClick={handleClick}
        className={clsx(
          'group bg-neutral-0 w-full cursor-pointer border-t border-neutral-100 px-2 py-4',
        )}
      >
        <div className="flex w-full items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <IconMoreHorizontalSquare className="h-4 w-4 text-neutral-900" />
            <div className="flex items-center gap-1">
              <span className="text-button-sm text-neutral-900">Button Styles</span>
              <InfoTooltip text="Pre-set button designs you can apply throughout your page. Primary is for your main call-to-action. Secondary is for supporting actions. Accent is for less important options. Using consistent styles helps guide visitors to what you want them to do." />
            </div>
          </div>
          <IconArrowDown className="h-4 w-4 text-neutral-500" />
        </div>
      </CollapsibleTrigger>

      <CollapsibleContent className={clsx('bg-neutral-0 w-full py-3')}>
        <div className="flex flex-col gap-3">
          <div className="w-full">
            <StylesListItemButton
              currentData={getButton('button1')}
              onUpdate={(button) => onChangeButton('button1', button)}
              className=""
              text="Primary"
              onSizeChange={onButtonSizeChange}
            />
          </div>

          <div className="w-full">
            <StylesListItemButton
              currentData={getButton('button2')}
              onUpdate={(button) => onChangeButton('button2', button)}
              className=""
              text="Secondary"
              onSizeChange={onButtonSizeChange}
            />
          </div>

          <div className="w-full">
            <StylesListItemButton
              currentData={getButton('button3')}
              onUpdate={(button) => onChangeButton('button3', button)}
              className=""
              text="Accent"
              onSizeChange={onButtonSizeChange}
            />
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};
